import { CardGiftcard, ChevronRight, ConfirmationNumber, LocalOffer, Percent, Store, WarningAmber } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyContentComponent } from '../../../components/Content';
import ListPageLayout from '../../../components/ListPageLayout';
import { LoadingContent } from '../../../components/Loading';
import useDialog from '../../../hooks/useDialog';
import { useFetch } from '../../../hooks/useFecth';
import DialogOffer from '../Advertiser/components/DialogOffer';
import { SearchDrawerOffersAdmin } from './components/SearchDrawer';

function formatDateTime(value) {
    if (!value) return null;
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value);
        if (!isValid(d)) return null;
        return format(d, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
        return null;
    }
}

function offerTypes(item) {
    const types = [];
    if (item.isCashback) types.push({ key: 'cashback', label: 'Cashback' });
    if (item.isCoupon) types.push({ key: 'coupon', label: 'Cupom' });
    if (item.isVoucher) types.push({ key: 'voucher', label: 'Voucher' });
    return types;
}

function brokerLabel(channel) {
    const key = String(channel || '').toLowerCase();
    if (key === 'awin') return 'Awin';
    if (key === 'lomadee') return 'Lomadee';
    if (key === 'socialsoul' || key === 'social-soul') return 'SocialSoul';
    return channel || null;
}

function isExpired(item) {
    if (!item?.expirationAt) return false;
    return new Date(item.expirationAt) < new Date();
}

function advertiserLabel(item) {
    const name = String(item?.Advertiser?.name || '').trim();
    const business = String(item?.Advertiser?.businessName || '').trim();
    return name || business || (item?.advertiserId ? `Anunciante #${item.advertiserId}` : null);
}

function OfferItem({ item, viewMode, onOpen }) {
    const types = offerTypes(item);
    const broker = brokerLabel(item.offerChannel);
    const expired = isExpired(item);
    const expirationLabel = formatDateTime(item.expirationAt);
    const advertiser = advertiserLabel(item);

    const chips = (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            <Chip
                size="small"
                icon={<Store sx={{ fontSize: 16 }} />}
                label={advertiser || 'Sem anunciante'}
                color={advertiser ? 'default' : 'warning'}
                variant={advertiser ? 'filled' : 'outlined'}
            />
            <Chip
                size="small"
                label={item.status ? 'Ativa' : 'Inativa'}
                color={item.status ? 'success' : 'default'}
                variant="outlined"
            />
            {expired && <Chip size="small" label="Expirada" color="error" variant="outlined" />}
            {types.length
                ? types.map(t => <Chip key={t.key} size="small" label={t.label} variant="outlined" />)
                : <Chip size="small" label="Sem tipo" color="warning" variant="outlined" />}
            {broker ? (
                <Chip size="small" label={broker} color={broker === 'Awin' ? 'secondary' : 'info'} variant="outlined" />
            ) : (
                <Chip size="small" label="Sem broker" variant="outlined" />
            )}
            {!item.description && <Chip size="small" label="Sem descrição" color="warning" variant="outlined" />}
        </Stack>
    );

    const body = viewMode === 'cards' ? (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar src={item.image || item.Advertiser?.image} variant="rounded" sx={{ width: 48, height: 48 }}>
                    <LocalOffer fontSize="small" />
                </Avatar>
                <Box minWidth={0}>
                    <Typography fontWeight={700} noWrap>
                        {item.name || 'Oferta sem nome'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        #{item.id}
                        {advertiser ? ` · ${advertiser}` : ''}
                    </Typography>
                </Box>
            </Stack>
            {chips}
            {expirationLabel && (
                <Typography variant="caption" color={expired ? 'error.main' : 'text.secondary'}>
                    Validade {expirationLabel}
                </Typography>
            )}
        </Stack>
    ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 3 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Avatar
                src={item.image || item.Advertiser?.image}
                variant="rounded"
                sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, border: '1px solid', borderColor: 'divider' }}
            >
                <LocalOffer fontSize="small" />
            </Avatar>
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 600 }}>
                        {item.name || 'Oferta sem nome'}
                    </Typography>
                    <Chip label={`#${item.id}`} size="small" sx={{ fontWeight: 600 }} />
                </Stack>
                {chips}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} flexWrap="wrap">
                    {expirationLabel && (
                        <Typography variant="body2" color={expired ? 'error.main' : 'text.secondary'}>
                            Validade {expirationLabel}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <ChevronRight sx={{ fontSize: 24, color: 'action.active' }} />
            </Box>
        </Stack>
    );

    return (
        <Card variant="outlined" sx={viewMode === 'cards' ? { height: '100%' } : undefined}>
            <CardActionArea onClick={() => onOpen(item.id)} sx={{ p: viewMode === 'cards' ? 2 : { xs: 2, sm: 2.5 }, height: '100%' }}>
                {body}
            </CardActionArea>
        </Card>
    );
}

function KpiCard({ title, value, icon, selected, onClick, color = 'text.primary' }) {
    return (
        <Card
            variant="outlined"
            sx={{
                minWidth: 0,
                borderColor: selected ? 'primary.main' : 'divider',
                boxShadow: selected ? 2 : 'none',
            }}
        >
            <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {title}
                            </Typography>
                            <Box sx={{ color: 'action.active', opacity: 0.5, display: 'flex' }}>{icon}</Box>
                        </Stack>
                        <Typography variant="h3" fontWeight={600} sx={{ color }}>
                            {(value ?? 0).toLocaleString('pt-BR')}
                        </Typography>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

export default function Offers() {
    const [searchParams, setParams] = useSearchParams();
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const { handleOpen } = useDialog('#offer');

    const params = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);
    const { data, mutate, isFetching } = useFetch('offer', { params });
    const { data: stats, mutate: mutateStats } = useFetch('offer/stats');

    const pageSize = Number(searchParams.get('per_page') || searchParams.get('perPage')) || 10;

    const hasFilters = Array.from(searchParams.entries()).some(
        ([key, value]) => value !== '' && value !== null && key !== 'page' && key !== 'per_page' && key !== 'perPage',
    );

    const handleClearFilters = () => {
        const next = new URLSearchParams();
        if (pageSize !== 10) next.set('per_page', String(pageSize));
        setParams(next);
    };

    const applyFilter = patch => {
        const next = new URLSearchParams();
        if (pageSize !== 10) next.set('per_page', String(pageSize));
        Object.entries(patch).forEach(([key, value]) => {
            if (value !== '' && value != null) next.set(key, String(value));
        });
        next.set('page', '1');
        setParams(next);
    };

    const handlePage = (_, page) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', page);
        setParams(nextParams);
    };

    const handlePageSizeChange = nextSize => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('per_page', String(nextSize));
        nextParams.set('page', '1');
        setParams(nextParams);
    };

    const statusFilter = searchParams.get('status');
    const expirationFilter = searchParams.get('expiration');
    const typeFilter = searchParams.get('type');
    const missingAdvertiser = searchParams.get('missingAdvertiser') === 'true';
    const missingType = searchParams.get('type') === 'none' || searchParams.get('missingType') === 'true';
    const missingDescription = searchParams.get('missingDescription') === 'true';

    const alerts = [
        {
            key: 'missingAdvertiser',
            active: missingAdvertiser,
            count: stats?.missingAdvertiser ?? 0,
            label: 'sem anunciante',
            onClick: () => applyFilter({ missingAdvertiser: 'true' }),
        },
        {
            key: 'missingType',
            active: missingType,
            count: stats?.missingType ?? 0,
            label: 'sem tipo',
            onClick: () => applyFilter({ type: 'none' }),
        },
        {
            key: 'missingDescription',
            active: missingDescription,
            count: stats?.missingDescription ?? 0,
            label: 'sem descrição',
            onClick: () => applyFilter({ missingDescription: 'true' }),
        },
    ];

    const alertTotal = alerts.reduce((sum, item) => sum + (item.count || 0), 0);

    return (
        <>
            <ListPageLayout
                title="Ofertas"
                subtitle="Todas as ofertas do sistema, com filtros por broker, anunciante, status e tipo."
                counts={[
                    { label: 'ofertas', value: stats?.total ?? data?.numRows ?? 0, color: 'primary' },
                    { label: 'ativas', value: stats?.active ?? 0, color: 'success' },
                    { label: 'inativas', value: stats?.inactive ?? 0 },
                    { label: 'cashback', value: stats?.cashback ?? 0 },
                    { label: 'cupons', value: stats?.coupon ?? 0 },
                    { label: 'vouchers', value: stats?.voucher ?? 0 },
                ]}
                loading={isFetching && !data}
                filterActive={hasFilters}
                onClearFilters={hasFilters ? handleClearFilters : null}
                onFiltersClick={() => setDrawerOpen(true)}
                viewStorageKey="admin-offers-view"
                intro={
                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                gap: 1.5,
                            }}
                        >
                            <KpiCard
                                title="Ativas"
                                value={stats?.active}
                                icon={<CheckCircleOutlineIcon />}
                                selected={statusFilter === 'true' && expirationFilter === 'valid'}
                                onClick={() => applyFilter({ status: 'true', expiration: 'valid' })}
                                color="success.main"
                            />
                            <KpiCard
                                title="Inativas"
                                value={stats?.inactive}
                                icon={<PauseCircleOutlineIcon />}
                                selected={statusFilter === 'false'}
                                onClick={() => applyFilter({ status: 'false' })}
                            />
                            <KpiCard
                                title="Expirando (24h)"
                                value={stats?.expiring24h}
                                icon={<AccessTimeIcon />}
                                selected={expirationFilter === 'expiring24h'}
                                onClick={() => applyFilter({ expiration: 'expiring24h', status: 'true' })}
                                color="warning.main"
                            />
                            <KpiCard
                                title="Cashback"
                                value={stats?.cashback}
                                icon={<Percent />}
                                selected={typeFilter === 'cashback'}
                                onClick={() => applyFilter({ type: 'cashback' })}
                            />
                            <KpiCard
                                title="Cupons"
                                value={stats?.coupon}
                                icon={<ConfirmationNumber />}
                                selected={typeFilter === 'coupon'}
                                onClick={() => applyFilter({ type: 'coupon' })}
                            />
                            <KpiCard
                                title="Vouchers"
                                value={stats?.voucher}
                                icon={<CardGiftcard />}
                                selected={typeFilter === 'voucher'}
                                onClick={() => applyFilter({ type: 'voucher' })}
                            />
                        </Box>

                        {stats && alertTotal > 0 ? (
                            <Alert
                                severity="warning"
                                icon={<WarningAmber />}
                                sx={{ alignItems: 'center' }}
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap">
                                    <Typography variant="body2" fontWeight={600}>
                                        Alertas de cadastro
                                    </Typography>
                                    {alerts.map(item => (
                                        <Chip
                                            key={item.key}
                                            size="small"
                                            color="warning"
                                            variant={item.active ? 'filled' : 'outlined'}
                                            label={`${item.count.toLocaleString('pt-BR')} ${item.label}`}
                                            onClick={item.onClick}
                                        />
                                    ))}
                                </Stack>
                            </Alert>
                        ) : null}
                        {stats && alertTotal === 0 ? (
                            <Alert severity="success">Nenhum alerta de cadastro: todas as ofertas têm anunciante, tipo e descrição.</Alert>
                        ) : null}
                    </Stack>
                }
                isEmpty={!isFetching && data?.rows?.length === 0}
                empty={
                    <Stack spacing={2} alignItems="center">
                        <EmptyContentComponent />
                        <Typography color="text.secondary">
                            {hasFilters ? 'Nenhuma oferta com esses filtros.' : 'Nenhuma oferta cadastrada.'}
                        </Typography>
                        {hasFilters ? (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Limpar filtros
                            </Button>
                        ) : null}
                    </Stack>
                }
                pagination={{
                    page: data?.page || 1,
                    pages: data?.pages || 1,
                    total: data?.numRows ?? 0,
                    pageSize,
                    onChange: handlePage,
                    onPageSizeChange: handlePageSizeChange,
                    itemLabel: (data?.numRows ?? 0) === 1 ? 'oferta' : 'ofertas',
                }}
            >
                {({ viewMode }) =>
                    isFetching && !data ? (
                        <LoadingContent />
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {data?.rows?.map(item => (
                                <OfferItem key={item.id} item={item} viewMode={viewMode} onOpen={handleOpen} />
                            ))}
                        </Box>
                    )
                }
            </ListPageLayout>

            <SearchDrawerOffersAdmin open={isDrawerOpen} onClose={() => setDrawerOpen(false)} onClearFilters={handleClearFilters} />
            <DialogOffer
                mutate={async () => {
                    await Promise.all([mutate(), mutateStats()]);
                }}
            />
        </>
    );
}
